import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@design-system/Button/Button";

const MIN_VISIBLE = 1;
const MAX_VISIBLE = 5;
// Widths above this threshold gain one extra visible item per step.
const BASE_WIDTH = 500;
const WIDTH_PER_EXTRA_ITEM = 200;

const getMaxVisibleForWidth = (width: number): number => {
    const extraItems = Math.ceil((width - BASE_WIDTH) / WIDTH_PER_EXTRA_ITEM);

    return Math.min(
        MAX_VISIBLE,
        Math.max(MIN_VISIBLE, MIN_VISIBLE + extraItems),
    );
};

interface OverflowListProps<T> {
    items: T[];
    renderItems(visibleItems: T[]): React.ReactNode;
    onOverflowClick(hiddenItems: T[]): void;
    maxVisible?: number;
    containerRef?: React.RefObject<HTMLDivElement | null>;
    hideOverflowButton?: boolean;
}

export const OverflowList = <T,>({
    items,
    renderItems,
    onOverflowClick,
    maxVisible,
    containerRef,
    hideOverflowButton = false,
}: OverflowListProps<T>) => {
    const [autoMaxVisible, setAutoMaxVisible] = useState(2);

    const computedMaxVisible = maxVisible ?? autoMaxVisible;
    const hasOverflow = items.length > computedMaxVisible;

    const visibleItems = useMemo(
        () => (hasOverflow ? items.slice(0, computedMaxVisible) : items),
        [items, computedMaxVisible, hasOverflow],
    );

    // Not a layout effect: `containerRef` usually sits on a parent, whose ref
    // React attaches only after this child's layout effects run.
    useEffect(() => {
        const container = containerRef?.current;
        if (maxVisible != null || !container) {
            return;
        }

        const measure = () =>
            setAutoMaxVisible(getMaxVisibleForWidth(container.clientWidth));
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(container);
        return () => observer.disconnect();
    }, [containerRef, maxVisible]);

    return (
        <>
            {renderItems(visibleItems)}
            {hasOverflow && !hideOverflowButton && (
                <Button
                    variant="secondary"
                    size="md"
                    onClick={() =>
                        onOverflowClick(items.slice(computedMaxVisible))
                    }
                >
                    +{items.length - visibleItems.length} more
                </Button>
            )}
        </>
    );
};
